import { useEffect, useRef } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { Group, GroupProps } from '~/components/component-library'
import {
  MAX_SLIPPAGE_BPS,
  MIN_SLIPPAGE_BPS,
} from '~/components/forms/swap/hooks/useSwap'
import { bpsToPercentage, DEFAULT_SLIPPAGE_BPS } from '~/utils/swap'
import { cn } from '~/utils/tailwind-utils'

const NON_NUMERIC = /[^\d.]/g

const buildDecimalPattern = (decimals: number) => {
  if (decimals <= 0) return /^\d*$/

  return new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`)
}

const sanitizeDecimal = (rawValue: string, decimals: number): string | null => {
  const cleaned = rawValue.replace(NON_NUMERIC, '')

  if (cleaned === '') {
    return ''
  }

  const normalized = cleaned.startsWith('.') ? `0${cleaned}` : cleaned

  return buildDecimalPattern(decimals).test(normalized) ? normalized : null
}

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const formatDecimal = (value: number, decimals: number) =>
  Number(value.toFixed(decimals)).toString()

type SlippageInputProps = {
  inputName: string
  min?: number
  max?: number
  defaultValue?: string
  decimals?: number
  tooltip: GroupProps['tooltip']
}

export const SlippageInput = ({
  inputName,
  min = bpsToPercentage(MIN_SLIPPAGE_BPS),
  max = bpsToPercentage(MAX_SLIPPAGE_BPS),
  defaultValue = bpsToPercentage(DEFAULT_SLIPPAGE_BPS).toString(),
  decimals = 2,
  tooltip,
}: SlippageInputProps) => {
  const { control, setValue } = useFormContext()

  // Once the user has entered a value, we no longer enforce any default value changes
  const isUserControlledRef = useRef(false)
  useEffect(() => {
    if (!isUserControlledRef.current) {
      setValue(inputName, defaultValue)
    }
  }, [defaultValue, setValue, inputName])

  const value = useWatch({
    control,
    name: inputName,
    defaultValue,
  })

  const isInvalid = value < min || value > max

  const inputHandlers = {
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur()
      }
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitizedValue = sanitizeDecimal(e.target.value, decimals)

      if (sanitizedValue === null) {
        return
      }

      if (sanitizedValue === '') {
        setValue(inputName, '')
        return
      }

      isUserControlledRef.current = true
      setValue(inputName, sanitizedValue)
    },
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      const target = e.target
      setTimeout(() => target.select(), 0)
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      const sanitizedValue = sanitizeDecimal(e.target.value.trim(), decimals)

      if (!sanitizedValue) {
        isUserControlledRef.current = false // User cleared the field, allow auto slippage again
        setValue(inputName, defaultValue)
        return
      }

      const withoutTrailingDot = sanitizedValue.endsWith('.')
        ? sanitizedValue.slice(0, -1)
        : sanitizedValue

      if (withoutTrailingDot === '') {
        isUserControlledRef.current = false
        setValue(inputName, defaultValue)
        return
      }

      const numericValue = parseFloat(withoutTrailingDot)

      if (Number.isNaN(numericValue)) {
        isUserControlledRef.current = false
        setValue(inputName, defaultValue)
        return
      }

      const clampedValue = clampNumber(numericValue, min, max)

      setValue(inputName, formatDecimal(clampedValue, decimals))

      // Focus on the dialog so Cmd+Enter shortcut will work
      setTimeout(() => {
        const dialogElement = document.querySelector('[data-dialog-id]')
        if (dialogElement) {
          ;(dialogElement as HTMLElement)?.focus()
        }
      }, 0)
    },
  }

  return (
    <Group tooltip={tooltip} layout="horizontal" label="Slippage">
      <div
        className={cn(
          'group inline-flex cursor-text items-baseline rounded-lg py-1.5 pl-3 pr-3 text-sm underline decoration-gray-300 underline-offset-4 outline-none ring-1 ring-inset ring-transparent transition focus-within:no-underline hover:no-underline dark:decoration-gray-700',
          'hover:[&:not(:focus-within)]:ring-gray-200 hover:[&:not(:focus-within)]:dark:ring-gray-700',
          {
            'ring-red-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-red-500 dark:ring-red-500 dark:focus-within:bg-gray-900':
              isInvalid,
            'focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:bg-gray-900':
              !isInvalid,
          },
        )}
        onClick={(e) => {
          // Focus the input when clicking anywhere in the container
          const input = e.currentTarget.querySelector('input')
          input?.focus()
        }}
      >
        <input
          name={inputName}
          value={value ?? ''}
          type="text"
          inputMode="decimal"
          className={cn(
            'w-[4ch] min-w-[2ch] max-w-[5ch] bg-transparent text-right tabular-nums text-secondary outline-none focus:text-primary',
          )}
          aria-label="Slippage tolerance percentage"
          aria-describedby="slippage-tooltip"
          {...inputHandlers}
        />
        <span className="pointer-events-none select-none text-secondary group-focus-within:text-primary">
          %
        </span>
      </div>
    </Group>
  )
}
